import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, message, Spin, Alert, Tabs } from 'antd';
import { getLinkProfileList, getEinvoiceUser, getPendingOrder } from 'utils/apis/eInvoice';
import { StyledCard, ProfilesToolbar, CompaniesTeamTabs } from './eInvoice.styles';
import { withAppContext } from 'context/AppContext';
import { withRouter } from 'react-router-dom';
import { useGetHosts } from 'utils/apis/host';
import { BasicSelect } from 'components/FormSelection/FormSelection';
import LinkProfileCard from './components/LinkProfileCard';
import LinkProfileModal from './components/LinkProfileModal';
import CompanyProfile from './components/CompanyProfile';
import RegisterEinvoiceUserForm from './components/RegisterEinvoiceUserForm';
import EInvoicePaymentStatus from './components/eInvoicePaymentStatus';
import SubUsersPanel from './components/SubUsersPanel';

const MY_ACCOUNT_HOST_VALUE = '';

const EInvoice = props => {
  const { checkIsAdmin, user } = props;
  const isAdmin = checkIsAdmin();
  const [loading, setLoading] = useState(true);
  const [eInvoiceUser, setEInvoiceUser] = useState(null);
  const [linkProfileList, setLinkProfileList] = useState([]);
  const [linkProfileVisible, setLinkProfileVisible] = useState(false);
  const [pendingOrderId, setPendingOrderId] = useState(null);
  const [pendingOrderInitialValues, setPendingOrderInitialValues] = useState(null);

  const [selectCompanyProfile, setSelectCompanyProfile] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [retryId, setRetryId] = useState(null);
  const [selectedHostId, setSelectedHostId] = useState(null);
  const [hostEnrollmentError, setHostEnrollmentError] = useState(null);
  const [personalAccountHostId, setPersonalAccountHostId] = useState(null);
  const [companiesTeamTab, setCompaniesTeamTab] = useState('companies');
  const [addSubUserModalVisible, setAddSubUserModalVisible] = useState(false);

  const { data: hostOptions, isLoading: isLoadingHostOptions } = useGetHosts(hosts => hosts.map(host => ({ label: host.name, value: host._id })), {
    shouldFetch: isAdmin
  });

  const resolveHostNameById = useCallback(
    hostId => {
      if (!hostId || !hostOptions || !hostOptions.length) {
        return null;
      }
      const match = hostOptions.find(option => String(option.value) === String(hostId));
      return match ? match.label : null;
    },
    [hostOptions]
  );

  const roleHostId = useMemo(() => {
    const roleHost = user && user.roles && user.roles[0] && user.roles[0].host;
    if (!roleHost) {
      return null;
    }
    return typeof roleHost === 'string' ? roleHost : roleHost._id;
  }, [user]);

  const myAccountHostId = personalAccountHostId || roleHostId;

  const myAccountHostName = useMemo(() => {
    const resolvedFromAccount = resolveHostNameById(myAccountHostId);
    if (resolvedFromAccount) {
      return resolvedFromAccount;
    }

    const roleHost = user && user.roles && user.roles[0] && user.roles[0].host;
    if (roleHost && typeof roleHost === 'object' && roleHost.name) {
      return roleHost.name;
    }

    return null;
  }, [user, myAccountHostId, resolveHostNameById]);

  const hostSelectOptions = useMemo(() => {
    const otherHostOptions = (hostOptions || []).filter(option => !myAccountHostId || String(option.value) !== String(myAccountHostId));

    return [
      {
        label: myAccountHostName ? `My account (${myAccountHostName})` : 'My account',
        value: MY_ACCOUNT_HOST_VALUE
      },
      ...otherHostOptions
    ];
  }, [hostOptions, myAccountHostName, myAccountHostId]);

  const clearPaymentQueryParams = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete('status');
    url.searchParams.delete('orderId');
    window.history.replaceState({ path: url.toString() }, '', url.toString());
  }, []);

  const handleClearPaymentStatus = useCallback(() => {
    clearPaymentQueryParams();
    setPaymentStatus(null);
    setRetryId(null);
  }, [clearPaymentQueryParams]);

  const resetPendingOrderModal = useCallback(() => {
    setPendingOrderId(null);
    setPendingOrderInitialValues(null);
  }, []);

  const handleChangePlan = async orderId => {
    const res = await getPendingOrder(orderId);
    if (res && res.status >= 200 && res.status < 300) {
      setPendingOrderId(orderId);
      setPendingOrderInitialValues(res.data.projectData || res.data);
      setLinkProfileVisible(true);
      return;
    }

    message.error((res && res.data && res.data.message) || 'Unable to load pending signup.');
  };

  const clearCompanyProfileFromUrl = useCallback(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('_id')) {
      return;
    }
    url.searchParams.delete('_id');
    window.history.replaceState({ path: url.toString() }, '', url.toString());
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status');
    const orderId = urlParams.get('orderId');
    const hostIdFromUrl = urlParams.get('hostId');

    if (status) {
      setPaymentStatus(status);
    }

    if (orderId) {
      setRetryId(orderId);
    }

    if (hostIdFromUrl) {
      setSelectedHostId(hostIdFromUrl);
      GetEinvoiceUser(hostIdFromUrl);
    } else {
      GetEinvoiceUser(null);
    }
  }, []);

  const GetLinkProfileList = async () => {
    const res = await getLinkProfileList();
    if (res && res.status >= 200 && res.status < 300) {
      setLinkProfileList(res.data);

      if (window.location.search.includes('_id')) {
        const url = new URL(window.location.href);
        const id = url.searchParams.get('_id');
        setSelectCompanyProfile(res.data.find(item => item._id === id));
      }
    } else if (res && res.status === 404) {
      message.warning((res.data && res.data.message) || 'No company profiles linked to this e-Invoice account.');
    } else if (res) {
      message.error((res.data && res.data.message) || 'Failed to load company profiles.');
    } else {
      message.error('Failed to load company profiles.');
    }
  };

  const GetEinvoiceUser = async hostId => {
    const apiHostId = hostId === null || hostId === undefined || hostId === MY_ACCOUNT_HOST_VALUE ? undefined : hostId;
    setLoading(true);
    setHostEnrollmentError(null);
    setLinkProfileList([]);

    const res = await getEinvoiceUser(apiHostId);

    setLoading(false);

    if (res && res.status >= 200 && res.status < 300) {
      localStorage.setItem('userToken', res.data.userToken);

      setEInvoiceUser(res.data);
      if (!apiHostId) {
        if (res.data.hostId) {
          setPersonalAccountHostId(res.data.hostId);
        } else if (roleHostId) {
          setPersonalAccountHostId(roleHostId);
        }
      }
      GetLinkProfileList();
    } else if (res && res.status === 404) {
      setEInvoiceUser(null);
      if (apiHostId) {
        setHostEnrollmentError((res.data && res.data.message) || 'Host has not enrolled for e-Invoice');
      }
    } else if (res) {
      message.error((res.data && res.data.message) || 'Failed to load e-Invoice account.');
    } else {
      message.error('Failed to load e-Invoice account.');
    }
  };

  const handleHostChange = hostId => {
    const resolvedHostId = hostId === MY_ACCOUNT_HOST_VALUE ? null : hostId;
    setSelectedHostId(resolvedHostId);
    setSelectCompanyProfile(null);
    setAddSubUserModalVisible(false);
    clearCompanyProfileFromUrl();
    GetEinvoiceUser(resolvedHostId);
  };

  const showHostLoading = isAdmin && (isLoadingHostOptions || !hostOptions);
  const hostSelectValue = selectedHostId === null ? MY_ACCOUNT_HOST_VALUE : selectedHostId;
  const canManageTeam = isAdmin || Boolean(eInvoiceUser && !eInvoiceUser.isSubUser);

  const hostSelect =
    isAdmin && !showHostLoading ? (
      <BasicSelect label="Host" selections={hostSelectOptions} value={hostSelectValue} onChange={handleHostChange} placeholder="Select host" />
    ) : null;

  const linkProfileCard = eInvoiceUser ? (
    <LinkProfileCard
      data={linkProfileList}
      openLinkProfileModal={() => {
        resetPendingOrderModal();
        setLinkProfileVisible(true);
      }}
      setSelectCompanyProfile={setSelectCompanyProfile}
    />
  ) : null;

  return (
    <StyledCard>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {paymentStatus ? (
            <EInvoicePaymentStatus
              paymentStatus={paymentStatus}
              retryId={retryId}
              onClearPaymentStatus={handleClearPaymentStatus}
              onChangePlan={handleChangePlan}
            />
          ) : (
            <>
              {isAdmin && !selectCompanyProfile && !eInvoiceUser && <ProfilesToolbar>{hostSelect}</ProfilesToolbar>}

              {!selectCompanyProfile && hostEnrollmentError && (
                <Alert type="warning" message={hostEnrollmentError} showIcon style={{ marginBottom: 24 }} />
              )}

              {!selectCompanyProfile && eInvoiceUser ? (
                canManageTeam ? (
                  <CompaniesTeamTabs
                    activeKey={companiesTeamTab}
                    onChange={setCompaniesTeamTab}
                    tabBarExtraContent={
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {companiesTeamTab === 'team' ? (
                          <Button type="primary" icon="plus" onClick={() => setAddSubUserModalVisible(true)}>
                            Add Sub User
                          </Button>
                        ) : null}
                        {hostSelect}
                      </div>
                    }
                  >
                    <Tabs.TabPane tab="Companies" key="companies">
                      {linkProfileCard}
                    </Tabs.TabPane>
                    <Tabs.TabPane tab="Team" key="team">
                      <SubUsersPanel
                        embedded
                        hideAddButton
                        eInvoiceUser={eInvoiceUser}
                        currentUser={user}
                        hostId={selectedHostId || undefined}
                        canManage={canManageTeam}
                        addModalVisible={addSubUserModalVisible}
                        onAddModalVisibleChange={setAddSubUserModalVisible}
                      />
                    </Tabs.TabPane>
                  </CompaniesTeamTabs>
                ) : (
                  linkProfileCard
                )
              ) : selectCompanyProfile && eInvoiceUser ? (
                <CompanyProfile
                  selectCompanyProfile={selectCompanyProfile}
                  setSelectCompanyProfile={setSelectCompanyProfile}
                  hostId={selectedHostId}
                />
              ) : !selectCompanyProfile && !eInvoiceUser && !hostEnrollmentError ? (
                <RegisterEinvoiceUserForm />
              ) : null}
            </>
          )}

          <LinkProfileModal
            visible={linkProfileVisible}
            pendingOrderId={pendingOrderId}
            initialValues={pendingOrderInitialValues}
            onCancel={() => {
              setLinkProfileVisible(false);
            }}
            onAfterClose={resetPendingOrderModal}
            GetLinkProfileList={GetLinkProfileList}
          />
        </>
      )}
    </StyledCard>
  );
};

export default withAppContext(withRouter(EInvoice));
